import { useCallback, useEffect, useRef, useState } from 'react'
import type { SearchInputVariant } from '../SearchInput.types'

const DEFAULT_DEBOUNCE_DELAY = 200

type UseSearchInputValueParams = {
  value?: string
  defaultValue?: string
  minLength: number
  withDebounce?: number | boolean
  variant: SearchInputVariant
  onSearchAction?: (value: string) => void
  onResetAction?: () => void
}

type UseSearchInputValue = {
  currentValue: string
  // Called by react-aria on every value change, including the clear button
  handleValueChange: (nextValue: string) => void
  // Called by react-aria on Enter, and by the search button
  handleSubmit: () => void
  // Called by react-aria on Escape and on clear button press
  handleClear: () => void
}

// `true` and an omitted value both mean the 200ms default, `false` means no wait at all
function resolveDebounceDelay(withDebounce: number | boolean | undefined): number {
  if (withDebounce === undefined || withDebounce === true) {
    return DEFAULT_DEBOUNCE_DELAY
  }

  return withDebounce === false ? 0 : withDebounce
}

// Owns the value and the debounce window: react-aria's SearchField handles the field state and the
// keyboard shortcuts, but knows nothing about debouncing nor a minimum query length
export function useSearchInputValue({
  value,
  defaultValue,
  minLength,
  withDebounce,
  variant,
  onSearchAction,
  onResetAction,
}: UseSearchInputValueParams): UseSearchInputValue {
  const [currentValue, setCurrentValue] = useState(value ?? defaultValue ?? '')
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    if (value !== undefined) {
      setCurrentValue(value)
    }
  }, [value])

  useEffect(() => {
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current)
      }
    }
  }, [])

  const cancelPendingSearch = useCallback(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current)
      debounceRef.current = null
    }
  }, [])

  const triggerSearch = useCallback(
    (nextValue: string) => {
      if (nextValue.length >= minLength) {
        onSearchAction?.(nextValue)
      }
    },
    [minLength, onSearchAction],
  )

  const debounceDelay = resolveDebounceDelay(withDebounce)
  const searchesWhileTyping = variant === 'instant'

  const handleValueChange = useCallback(
    (nextValue: string) => {
      setCurrentValue(nextValue)
      cancelPendingSearch()

      if (searchesWhileTyping) {
        debounceRef.current = setTimeout(() => triggerSearch(nextValue), debounceDelay)
      }
    },
    [cancelPendingSearch, debounceDelay, searchesWhileTyping, triggerSearch],
  )

  const handleSubmit = useCallback(() => {
    cancelPendingSearch()
    triggerSearch(currentValue)
  }, [cancelPendingSearch, currentValue, triggerSearch])

  const handleClear = useCallback(() => {
    cancelPendingSearch()
    setCurrentValue('')
    onResetAction?.()

    // Replaying an empty search only makes sense when it was running while typing
    if (searchesWhileTyping) {
      triggerSearch('')
    }
  }, [cancelPendingSearch, onResetAction, searchesWhileTyping, triggerSearch])

  return { currentValue, handleValueChange, handleSubmit, handleClear }
}
